import { useCallback, useEffect, useState } from 'react';

import { vnextApiClient } from '@/services/vnext-api';
import { ReportSummary } from '@/types/report';

interface UseReportsParams {
  limit?: number;
  page?: number;
  /** Combined substring search: subject, requester email, codename,
   * report type — and, for admins, network partner name/company/email. */
  search?: string;
  sort?: string;
  status?: string;
  /** Admin filter: exact team id. */
  teamId?: string;
  /** Screening hit rating: hit / potential / clear / unrated. */
  rating?: string;
  autoFetch?: boolean;
}

export function useReports(params: UseReportsParams = {}) {
  const {
    limit = 10,
    page = 1,
    search = '',
    sort = '',
    status = '',
    teamId = '',
    rating = '',
    autoFetch = true,
  } = params;

  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);

  const fetchReports = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await vnextApiClient.listReports({
        limit,
        page,
        search: search || undefined,
        sort: sort || undefined,
        status: status || undefined,
        team_id: teamId || undefined,
        rating: rating || undefined,
      });

      setReports(response.items);
      setTotal(response.total);
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch reports');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [limit, page, search, sort, status, teamId, rating]);

  useEffect(() => {
    if (autoFetch) {
      fetchReports();
    }
  }, [autoFetch, fetchReports]);

  return {
    reports,
    isLoading,
    error,
    total,
    fetchReports,
  };
}
